import React, { useState, useEffect } from 'react';
import { apiKeysAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Key, Copy, Trash2, RefreshCw, Plus, AlertTriangle, CheckCircle } from 'lucide-react';

export default function APIKeys() {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showKeyDisplayDialog, setShowKeyDisplayDialog] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [generatedKey, setGeneratedKey] = useState(null);
  const [selectedKeyId, setSelectedKeyId] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadAPIKeys();
  }, []);

  const loadAPIKeys = async () => {
    try {
      setLoading(true);
      const response = await apiKeysAPI.getAll();
      setApiKeys(response.data || []);
    } catch (error) {
      console.error('Failed to load API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    if (!keyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }

    try {
      setIsGenerating(true);
      const response = await apiKeysAPI.create({ name: keyName.trim() });
      
      // Store the full key for one-time display
      setGeneratedKey(response.data);
      
      // Close generate dialog and show key display dialog
      setShowGenerateDialog(false);
      setShowKeyDisplayDialog(true);
      setKeyName('');
      
      // Reload the key list
      await loadAPIKeys();
      
      toast.success('API key generated successfully');
    } catch (error) {
      console.error('Failed to generate API key:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate API key');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerateKey = async () => {
    try {
      const response = await apiKeysAPI.regenerate();
      
      // Store the new key for display
      setGeneratedKey(response.data);
      
      // Close regenerate dialog and show key display dialog
      setShowRegenerateDialog(false);
      setShowKeyDisplayDialog(true);
      
      // Reload the key list
      await loadAPIKeys();
      
      toast.success('API key regenerated successfully');
    } catch (error) {
      console.error('Failed to regenerate API key:', error);
      toast.error(error.response?.data?.detail || 'Failed to regenerate API key');
    }
  };

  const handleDeleteKey = async () => {
    try {
      await apiKeysAPI.delete(selectedKeyId);
      setShowDeleteDialog(false);
      setSelectedKeyId(null);
      await loadAPIKeys();
      toast.success('API key deleted successfully');
    } catch (error) {
      console.error('Failed to delete API key:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete API key');
    }
  };

  const handleCopyKey = async (key) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopied(true);
      toast.success('API key copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy API key');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openRegenerateDialog = () => {
    setShowRegenerateDialog(true);
  };

  const openDeleteDialog = (keyId) => {
    setSelectedKeyId(keyId);
    setShowDeleteDialog(true);
  };

  return (
    <div className="space-y-6" data-testid="api-keys-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="page-title">API Keys</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your API keys for integration</p>
        </div>
        <Button 
          onClick={() => setShowGenerateDialog(true)} 
          className="gap-2"
          data-testid="generate-api-key-button"
        >
          <Plus className="h-4 w-4" />
          Generate New Key
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Your API Keys
          </CardTitle>
          <CardDescription>
            Use these keys to authenticate your API requests. Keep them secure and never share them publicly.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                  <Skeleton className="h-9 w-24" />
                </div>
              ))}
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-12" data-testid="empty-state">
              <div className="mx-auto w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-4">
                <Key className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">No API keys yet</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Generate your first API key to start integrating with our API
              </p>
              <Button onClick={() => setShowGenerateDialog(true)} data-testid="empty-state-generate-button">
                <Plus className="h-4 w-4 mr-2" />
                Generate API Key
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Key Preview</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Last Used</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apiKeys.map((key) => (
                    <TableRow key={key.id} data-testid={`api-key-row-${key.id}`}>
                      <TableCell className="font-medium">{key.name}</TableCell>
                      <TableCell>
                        <code className="text-xs bg-secondary px-2 py-1 rounded">
                          {key.key_preview}
                        </code>
                      </TableCell>
                      <TableCell>
                        <Badge variant={key.is_active ? "success" : "secondary"}>
                          {key.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(key.created_at)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(key.last_used_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteDialog(key.id)}
                            data-testid={`delete-key-button-${key.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {apiKeys.length > 0 && (
            <div className="mt-6 pt-6 border-t">
              <Button
                variant="outline"
                onClick={openRegenerateDialog}
                className="gap-2"
                data-testid="regenerate-key-button"
              >
                <RefreshCw className="h-4 w-4" />
                Regenerate API Key
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Regenerating will deactivate your current key and create a new one.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generate Key Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent data-testid="generate-dialog">
          <DialogHeader>
            <DialogTitle>Generate New API Key</DialogTitle>
            <DialogDescription>
              Create a new API key for authenticating your requests. You'll only see the full key once.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="key-name">Key Name</Label>
              <Input
                id="key-name"
                placeholder="e.g., Production Server"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                data-testid="key-name-input"
              />
              <p className="text-xs text-muted-foreground">
                Choose a descriptive name to identify where this key is used.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowGenerateDialog(false);
                setKeyName('');
              }}
              data-testid="cancel-generate-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerateKey}
              disabled={isGenerating}
              data-testid="confirm-generate-button"
            >
              {isGenerating ? 'Generating...' : 'Generate Key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Key Display Dialog (One-time display) */}
      <Dialog open={showKeyDisplayDialog} onOpenChange={setShowKeyDisplayDialog}>
        <DialogContent className="max-w-2xl" data-testid="key-display-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              API Key Generated Successfully
            </DialogTitle>
            <DialogDescription>
              Copy your API key now. For security reasons, you won't be able to see it again.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Your API Key</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-secondary px-4 py-3 rounded text-sm font-mono break-all" data-testid="generated-key-display">
                  {generatedKey?.key}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopyKey(generatedKey?.key)}
                  data-testid="copy-key-button"
                >
                  {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                    Important Security Notice
                  </p>
                  <p className="text-xs text-amber-800 dark:text-amber-200">
                    This is the only time you'll see the full API key. Make sure to copy it now and store it securely.
                    If you lose it, you'll need to generate a new one.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setShowKeyDisplayDialog(false);
                setGeneratedKey(null);
              }}
              data-testid="close-key-display-button"
            >
              I've Saved My Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate Confirmation Dialog */}
      <Dialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
        <DialogContent data-testid="regenerate-dialog">
          <DialogHeader>
            <DialogTitle>Regenerate API Key?</DialogTitle>
            <DialogDescription>
              This will deactivate your current API key and create a new one. Any applications using the old key will stop working.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                  Warning
                </p>
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  Your old API key will be permanently deactivated and cannot be recovered.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRegenerateDialog(false)}
              data-testid="cancel-regenerate-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRegenerateKey}
              data-testid="confirm-regenerate-button"
            >
              Regenerate Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete API Key?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. Any applications using this key will stop working.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setSelectedKeyId(null);
              }}
              data-testid="cancel-delete-button"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteKey}
              data-testid="confirm-delete-button"
            >
              Delete Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
